import {
  createContextMutationPrecondition,
  parseContextSession,
  type ContextSessionResponse,
  type ControlWebClient,
  type RequestOptions,
} from "@kontextwerk/control-web-client"
import { draft, invalid, orderType, restoreResult, uuid } from "./_logic/contracts"

type Capture = Required<Pick<RequestOptions, "signal" | "isCurrent" | "mutationContext">>
/** The table and every overlay retain the context that initiated them, including reads. */
export const createOrderTypesContextApi = (
  client: ControlWebClient,
  value: ContextSessionResponse,
  capture: Capture,
) => {
  const session = parseContextSession(value)
  const pair = createContextMutationPrecondition(capture.mutationContext)

  if (
    session.product_issuer !== "kehrblatt-control" ||
    session.context.kind !== "organization" ||
    session.session_id !== pair.expected_session_id ||
    session.context_revision !== pair.expected_context_revision
  )
    return invalid()

  const organizationId = session.context.organization_id
  const path = "/admin/order-types"
  const current = (signal: AbortSignal) => {
    if (capture.signal.aborted || signal.aborted || !capture.isCurrent())
      throw new DOMException("Obsolete order-type context", "AbortError")
  }
  const request = async (
    method: "GET" | "POST" | "PATCH" | "DELETE",
    route: string,
    signal: AbortSignal,
    json?: unknown,
  ) => {
    current(signal)
    const result = await client.transport.request<unknown>(method, route, {
      ...capture,
      mutationContext: pair,
      signal: AbortSignal.any([capture.signal, signal]),
      retry: "never",
      ...(method === "GET" ? {} : { requireCsrf: true }),
      ...(json === undefined ? {} : { json }),
    })
    current(signal)

    return result
  }

  return {
    async list(signal: AbortSignal) {
      const result = await request(
        "GET",
        `${path}?${new URLSearchParams({ organization_id: organizationId })}`,
        signal,
      )

      if (!Array.isArray(result)) return invalid()

      const rows = result.map((item) => orderType(item, organizationId))

      if (new Set(rows.map((row) => row.id)).size !== rows.length) return invalid()

      return rows
    },
    async create(payload: OrderTypeUpdatePayload, signal: AbortSignal) {
      const checked = draft(payload)

      if (!checked.internal_label || !checked.public_label) return invalid()

      return orderType(
        await request("POST", path, signal, { ...checked, organization_id: organizationId }),
        organizationId,
      )
    },
    async update(id: string, payload: OrderTypeUpdatePayload, signal: AbortSignal) {
      const result = orderType(
        await request("PATCH", `${path}/${uuid(id)}`, signal, draft(payload)),
        organizationId,
      )

      if (result.id !== id) return invalid()

      return result
    },
    async remove(id: string, signal: AbortSignal) {
      await request("DELETE", `${path}/${uuid(id)}`, signal)
    },
    async restore(signal: AbortSignal) {
      return restoreResult(
        await request("POST", `${path}/reset-defaults`, signal, {
          organization_id: organizationId,
        }),
      )
    },
  }
}
